import { useRef, useState } from 'react';
import { useConversation } from '@elevenlabs/react';
import { Loader2 } from 'lucide-react';
import { toast } from 'react-toastify';
import axiosInstance from '../../utils/axiosConfig';

const AGENT_ID = 'agent_1501kk4vf4m7e7c81mtjp44ak0nx';

// Agent line = question, the patient's reply = answer (the shape assessment views render).
const toQA = (turns) => turns.reduce((pairs, { role, text }) => {
  const last = pairs[pairs.length - 1];
  if (role === 'agent') pairs.push({ question: text, answer: '' });
  else if (last && !last.answer) last.answer = text;
  else pairs.push({ question: '', answer: text });
  return pairs;
}, []);

// Replaces the hosted ElevenLabs widget, which never sent the conversation back to us.
// When the call ends the transcript is saved as an assessment, so the ER dashboard and
// the MD-FAQ agent can read it straight away.
export default function EmergencyAgent() {
  const turns = useRef([]);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    const transcript = turns.current;
    turns.current = [];
    if (!transcript.some((t) => t.role === 'patient')) return;
    try {
      await axiosInstance.post('/assessments/create', {
        name: 'Emergency Assessment',
        type: 'Emergency',
        data: toQA(transcript),
        summary: transcript.map((t) => `${t.role === 'agent' ? 'Agent' : 'Patient'}: ${t.text}`).join('\n'),
      });
      toast.success('Your emergency assessment was sent to the care team.');
    } catch {
      toast.error('Could not send your emergency assessment. If this is urgent, call emergency services.');
    }
  };

  const conversation = useConversation({
    onMessage: ({ source, message }) => {
      if (message) turns.current.push({ role: source === 'user' ? 'patient' : 'agent', text: message });
    },
    onDisconnect: save,
    onError: (error) => toast.error(typeof error === 'string' ? error : error?.message || 'Emergency agent connection failed.'),
  });
  const live = conversation.status === 'connected';

  const toggle = async () => {
    setBusy(true);
    try {
      if (live) await conversation.endSession();
      else {
        await navigator.mediaDevices.getUserMedia({ audio: true });
        await conversation.startSession({ agentId: AGENT_ID, connectionType: 'websocket' });
      }
    } catch (error) {
      toast.error(error?.message || 'Could not start the emergency agent.');
    } finally {
      setBusy(false);
    }
  };

  const label = busy ? 'Connecting to emergency agent' : live ? 'End emergency call' : 'Emergency help';

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={busy}
      aria-pressed={live}
      aria-label={label}
      title={label}
      className={`fixed bottom-5 left-5 z-[1000] flex h-16 w-16 items-center justify-center rounded-full bg-white shadow-lg ring-4 transition-transform hover:scale-105 disabled:opacity-60 ${live ? 'ring-red-600 animate-pulse' : 'ring-red-500'}`}
    >
      {busy
        ? <Loader2 className="h-7 w-7 animate-spin text-red-600" aria-hidden="true" />
        : <img src="/emergency-ambulance.png" alt="" className="h-10 w-10" />}
    </button>
  );
}
